import { VdTracking } from '@vend/vend.js'
import { getRetailer, initRaygun } from '@vend/utilities'
import { ApiUser } from '@vend/types'
import { env } from '../../../../lib/env'

import { $injector, $q } from '../../ng-compat/imports'
import { getCoreTrackingConfig } from './getCoreTrackingConfig'

const RAYGUN_API_KEY = 'm7xNxyU82ueCe4bOWpXn9w=='
const C_TRADING_TAG_ID = 'UA-171730176-1'

/**
 * Service that initialises and configures all forms of tracking used within webregister - UBE events, error tracking
 * and intercom.
 *
 * @class Tracking
 */
export class Tracking {
  private _vdTracking: VdTracking
  private _whenInitialised: $TSFixMe

  constructor({ vdTracking }: { vdTracking: VdTracking }) {
    this._vdTracking = vdTracking
  }

  /**
   * Initialise the various trackers.
   *
   * @method init
   * @return {Promise}
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = $injector.invoke([
        'posStateManager',
        'userManager',
        (posStateManager, userManager) => {
          return $q
            .all([posStateManager.init(), userManager.init(), getRetailer()])
            .then(([, , retailer]) =>
              this._vdTracking.init({
                deviceFingerprint: posStateManager.getClientId(),
                retailerId: retailer.id,
                retailerAccountStatus: retailer.account_status,
                userId: userManager.getLoggedInUser().id,
              })
            )
        },
      ])
    }

    return this._whenInitialised
  }

  /**
   * Update the user details being tracked by intercom and UBE events.
   *
   * @method setUser
   *
   * @param  {User} user
   *         The new user
   */
  public setUser(user: ApiUser) {
    this.init().then(() => {
      this._vdTracking.updateUser(user)
    })
  }
}

const commonErrorPrefixes = [
  'Application is offline',
  'Failed to fetch',
  'Possibly unhandled rejection:',
]

/**
 * Raygun hook that runs before an error is submitted.
 * It filters out 95% of common errors.
 * Between the AngularJS 1.7 upgrade and some React Navigation issues, we're getting many duplicate error reports.
 *
 * @param {Object} payload
 */
const onBeforeSend = (payload: any) => {
  // Campus Trading uses Tamper Monkey which generates a substantial amount of errors
  const hasKnownExternalScripts = Boolean(
    (window as any).google_tag_manager?.[C_TRADING_TAG_ID]
  )

  if (hasKnownExternalScripts) {
    return false
  }

  const message = payload.Details.Error.Message
  if (commonErrorPrefixes.some(prefix => message.startsWith(prefix))) {
    // Common error; sample at 5%
    return Math.random() < 0.05 ? payload : false
  }

  const stackString = payload.Details.Error.StackString

  if (stackString && stackString.match(/(safari|chrome)-extension/)) {
    return false
  }

  return payload
}

if (env.isProduction()) {
  const coreTracking = getCoreTrackingConfig()
  getRetailer()
    .then(retailer =>
      initRaygun(RAYGUN_API_KEY, {
        ...coreTracking,
        environment: 'production',
        onBeforeSend,
        saveIfOffline: true,
        retailerId: retailer.id,
        retailerAccountStatus: retailer.account_status,
      })
    )
    .catch(() =>
      initRaygun(RAYGUN_API_KEY, {
        ...coreTracking,
        environment: 'production',
        onBeforeSend,
        saveIfOffline: true,
      })
    )
}

/**
 * Provider for the Tracking service.
 *
 * @class TrackingProvider
 */
export class TrackingProvider {
  public $get: $TSFixMe
  constructor($windowProvider: $TSFixMe, vdTrackingProvider: $TSFixMe) {
    'ngInject'

    const retailerDomainPrefix = $windowProvider.$get().location.hostname

    vdTrackingProvider.configure({
      ...getCoreTrackingConfig(),
      retailerDomainPrefix,
      batchingTimeout: 1000,
    })

    this.$get = function (vdTracking: VdTracking) {
      'ngInject'

      return new Tracking({ vdTracking })
    }
  }
}
